import { Form, Formik, type FormikHelpers } from "formik";
import orderBy from "lodash-es/orderBy";
import range from "lodash-es/range";
import { computed, observable, makeObservable } from "mobx";
import { inject, observer } from "mobx-react";
import { Component } from "react";
import { FormattedMessage, type IntlShape, injectIntl } from "react-intl";
import { compose } from "nvent-web/utils/compose";
import * as yup from "yup";

import LinkButton from "nvent-web/components/Button/LinkButton";
import { DropdownField } from "nvent-web/components/form/DropdownField";
import SubmitCancelButtons from "nvent-web/components/SubmitCancelButtons";
import { ProductsStore } from "nvent-web/stores/Products";
import { CategoryType } from "nvent-web/types/CategoryType";
import { type DropdownOption } from "nvent-web/types/DropdownOption";
import { type Product } from "nvent-web/types/Product";
import { type ProductCategory } from "nvent-web/types/ProductCategory";
import { type ProductSku } from "nvent-web/types/ProductSku";

import style from "./ByCategoryForm.module.scss";
import { withRouter, type WithRouterProps } from "~/nvent-web/utils/withRouter";

interface FormValues {
  categoryId: string;
  productId: string;
  productSkuId: string;
  length?: string;
}

const initialValues: FormValues = {
  categoryId: "",
  productId: "",
  productSkuId: "",
  length: "",
};

interface ByCategoryFormProps {
  onSubmit: (productSkuId: number, length?: number) => Promise<void>;
  onClose: () => void;
}

interface ByCategoryFormInnerProps
  extends ByCategoryFormProps, WithRouterProps {
  products: ProductsStore;
  intl: IntlShape;
}

class ByCategoryFormInner extends Component<ByCategoryFormInnerProps> {
  constructor(props: ByCategoryFormInnerProps) {
    super(props);

    makeObservable<
      ByCategoryFormInner,
      | "categoryOptions"
      | "selectedCategoryId"
      | "selectedCategory"
      | "products"
      | "productOptions"
      | "selectedProductId"
      | "selectedProduct"
      | "productSkus"
      | "productSkuOptions"
      | "isLengthRequired"
      | "schema"
    >(this, {
      categoryOptions: computed,
      selectedCategoryId: observable,
      selectedCategory: computed,
      products: computed,
      productOptions: computed,
      selectedProductId: observable,
      selectedProduct: computed,
      productSkus: computed,
      productSkuOptions: computed,
      isLengthRequired: computed,
      schema: computed,
    });
  }

  private get categoryOptions(): DropdownOption[] {
    const categories =
      this.props.products.availableProductCategoriesByType[
        CategoryType.product
      ];

    return categories.map((category) => ({
      title: category.identifier,
      key: category.id.toString(),
    }));
  }

  private selectedCategoryId?: number;

  private get selectedCategory(): ProductCategory | undefined {
    const { products } = this.props;

    return products.availableProductCategories.find(
      (category) => category.id === this.selectedCategoryId
    );
  }

  private get products(): Product[] {
    const products = this.props.products.availableProducts.filter(
      ({ productCategoryId }) => productCategoryId === this.selectedCategoryId
    ) as Product[];

    return orderBy(products, [
      ({ cableLength }) => cableLength || Infinity,
      ({ totalOutput }) => totalOutput || Infinity,
    ]);
  }

  private get productOptions(): DropdownOption[] {
    return this.products.map(({ id, description }) => ({
      title: description,
      key: id.toString(),
    }));
  }

  private selectedProductId?: number;

  private get selectedProduct(): Product | undefined {
    return this.products.find(({ id }) => id === this.selectedProductId);
  }

  private get productSkus(): ProductSku[] {
    return this.props.products.availableProductSkus.filter(
      ({ productId }) => productId === this.selectedProductId
    );
  }

  private get productSkuOptions(): DropdownOption[] {
    return this.productSkus.map(({ id, description }) => ({
      title: description,
      key: id.toString(),
    }));
  }

  private get isLengthRequired(): boolean {
    return Boolean(this.selectedProduct && this.selectedProduct.customLength);
  }

  private readonly productLengthOptions = range(1, 101).map((value) => ({
    title: `${value} m`,
    key: value.toString(),
  }));

  private get schema() {
    let length = yup.string();
    if (this.isLengthRequired) {
      length = length.required();
    }

    let productSkuId = yup.string();
    if (this.productSkuOptions.length > 1) {
      productSkuId = productSkuId.required();
    }

    return yup.object({
      categoryId: yup.string().required(),
      productId: yup.string().required(),
      productSkuId,
      length,
    });
  }

  private isFormValid(values: FormValues): boolean {
    try {
      this.schema.validateSync(values, { abortEarly: false });
      return true;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_error) {
      return false;
    }
  }

  private get areEmptyCategoryOptions() {
    return this.categoryOptions.length === 0;
  }

  render() {
    const { intl } = this.props;
    return (
      <Formik
        initialValues={initialValues}
        validationSchema={this.schema}
        onSubmit={this.handleSubmit}
      >
        {({ isSubmitting, setFieldValue, setFieldTouched, values }) => (
          <Form noValidate={true}>
            <p className={style.notice}>
              <FormattedMessage id="product.add.byCategory.description" />
            </p>

            <DropdownField
              name="categoryId"
              disabled={this.areEmptyCategoryOptions}
              label={<FormattedMessage id="form.productCategory" />}
              options={this.categoryOptions}
              onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                this.onCategoryChange(event);
                setFieldValue("productId", "");
                setFieldTouched("productId", false);
              }}
              placeholder={intl.formatMessage({
                id: "form.productCategory.selectPlaceholder",
              })}
              required
            />

            {this.areEmptyCategoryOptions && (
              <p className={style.warning}>
                <FormattedMessage id="form.productCategory.noCategoryOptions" />
              </p>
            )}

            {this.selectedCategory && (
              <>
                <DropdownField
                  name="productId"
                  label={intl.formatMessage({ id: "form.productName" })}
                  options={this.productOptions}
                  onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                    this.onProductChange(event);
                    setFieldValue("productSkuId", "");
                    setFieldValue("length", "");
                    setFieldTouched("productSkuId", false);
                    setFieldTouched("length", false);
                  }}
                  placeholder={intl.formatMessage({
                    id: "form.productName.selectPlaceholder",
                  })}
                  required
                />
              </>
            )}

            {this.selectedProduct && this.productSkuOptions.length > 1 && (
              <>
                <DropdownField
                  name="productSkuId"
                  label={intl.formatMessage({ id: "form.productSku" })}
                  options={this.productSkuOptions}
                  placeholder={intl.formatMessage({
                    id: "form.productSku.selectPlaceholder",
                  })}
                  required
                />
              </>
            )}

            {this.isLengthRequired && (
              <DropdownField
                name="length"
                label={intl.formatMessage({ id: "form.productLength.label" })}
                options={this.productLengthOptions}
                placeholder={intl.formatMessage({
                  id: "form.productLength.selectPlaceholder",
                })}
                required
              />
            )}
            <div className={style.actions}>
              <SubmitCancelButtons
                onCancel={this.handleCancel}
                submitLabel={<FormattedMessage id="actions.add" />}
                disabled={!this.isFormValid(values)}
                loading={isSubmitting}
              />
            </div>
            <LinkButton
              to="/selection-guide"
              theme="primaryBlue"
              className={style.selectionGuideBtn}
            >
              <FormattedMessage id="form.productCategory.selectionGuide" />
            </LinkButton>
          </Form>
        )}
      </Formik>
    );
  }

  private onCategoryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    this.selectedCategoryId = Number(event.target.value);
  };

  private onProductChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    this.selectedProductId = Number(event.target.value);
  };

  private handleCancel = () => {
    this.props.onClose();
  };

  private handleSubmit = async (
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>
  ) => {
    const length = this.isLengthRequired ? Number(values.length) : undefined;
    const productSkuId = values.productSkuId
      ? Number(values.productSkuId)
      : this.productSkus[0].id;

    try {
      await this.props.onSubmit(productSkuId, length);
    } finally {
      setSubmitting(false);
    }
  };
}

const ByCategoryForm = compose<ByCategoryFormProps>(
  injectIntl,
  inject("products"),
  observer,
  withRouter
)(ByCategoryFormInner);

export default ByCategoryForm;
